import { BonusPoint } from "@app/secured/GradebookDayPage/BonusPoint";
import {
  ActivityStatus,
  ActivityType,
  AssetComponentMetadata,
  Button,
  ConfirmDialog,
  DropDownMenu,
  GradeActivityMetadata,
  NumberField,
  RoleType,
} from "@cca/components";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

export type GradePanelProps = {
  metadata?: AssetComponentMetadata;
  gradeMetadata?: GradeActivityMetadata;
  gradeVisible?: boolean;
  onModifierChange?: (
    newModifier: number | undefined,
    activityId?: string,
  ) => void;
  pendingSubmit?: boolean;
  pendingReset?: boolean;
  pendingEdit?: boolean;
  onSubmitActivityGrade?: () => Promise<void>;
  onResetActivity?: () => Promise<void>;
  onEditActivity?: () => Promise<void>;
};
const validInputRegex = /^(\-)?(0|([1-9]\d{0,12}))((\.\d{0,1}))?$/;

export const GradePanel: React.FC<GradePanelProps> = ({
  metadata,
  gradeMetadata,
  gradeVisible,
  pendingSubmit,
  pendingReset,
  pendingEdit,
  onEditActivity,
  onModifierChange,
  onSubmitActivityGrade,
  onResetActivity,
}) => {
  const { role, activityStatus, activityType, activityName } = metadata || {};

  const { activityId } = useParams<{ activityId: string }>();
  const modifierNumberStep = 0.5;

  const [resetConfirmModal, setResetConfirmModal] = useState<boolean>(false);

  const activityIsPendingGrade = activityStatus === ActivityStatus.PendingGrade;

  const activityIsCompleted =
    activityStatus === ActivityStatus.Completed ||
    activityStatus === ActivityStatus.Modified;

  useEffect(() => {
    if (pendingReset) return;

    setResetConfirmModal(false);
  }, [pendingReset, setResetConfirmModal]);

  const {
    courseIsCompleted,
    pointsCalculated,
    pointsAvailable,
    gradePercentage,
    pointsEarned,
    pointsModifier,
  } = gradeMetadata || {};

  const modifierNumberIsDisabled =
    !activityIsPendingGrade ||
    (pointsCalculated !== undefined &&
      pointsAvailable !== undefined &&
      pointsModifier !== undefined &&
      pointsAvailable - pointsCalculated < pointsModifier);

  const dropDownItems = useMemo(
    () => [
      {
        id: "reset",
        label: "Reset",
        disabled:
          (!activityIsCompleted && !activityIsPendingGrade) ||
          courseIsCompleted,
        onClick: () => setResetConfirmModal(true),
      },
    ],
    [activityIsCompleted, activityIsPendingGrade, courseIsCompleted],
  );

  const handleNumberChange = (value: string | undefined) => {
    if (!value) {
      onModifierChange?.(undefined, activityId);
      return;
    }
    if (!validInputRegex.test(value)) return;
    const parsedValue = parseFloat(value);
    if (isNaN(parsedValue) || parsedValue % modifierNumberStep !== 0) {
      return;
    }
    if (pointsCalculated !== undefined && pointsAvailable != null) {
      if (parsedValue < -pointsCalculated) {
        onModifierChange?.(-pointsCalculated, activityId);
        return;
      }
      const maxValue = pointsAvailable! - pointsCalculated;
      if (parsedValue > maxValue) {
        onModifierChange?.(maxValue, activityId);
        return;
      }
    }

    onModifierChange?.(parsedValue, activityId);
  };

  const learnerCanViewGrade = gradeVisible && !!pointsAvailable;

  if (
    metadata?.activityStatus === undefined ||
    (metadata?.role === RoleType.Learner && !learnerCanViewGrade)
  )
    return null;

  return (
    <>
      <div className="flex min-h-16 w-full flex-wrap justify-between gap-x-4 gap-y-2 border-b border-gray-100 px-4 py-2 text-sm">
        <div className="flex flex-row items-center gap-4">
          {activityType === ActivityType.Assessment && (
            <>
              <div className="flex flex-wrap items-center gap-x-2">
                <span className="text-nowrap text-gray-800">
                  Total Points:{" "}
                </span>
                <span className="font-bold">
                  {pointsEarned ?? "-"} / {pointsAvailable ?? "-"}
                </span>
              </div>
              {!!pointsModifier && (
                <div className="flex flex-wrap items-center gap-x-2">
                  <span className="text-nowrap text-gray-800">
                    Bonus Points:
                  </span>
                  <BonusPoint points={pointsModifier} />
                </div>
              )}
              <div className="flex flex-wrap gap-x-2">
                <span className="text-nowrap text-gray-800">Grade: </span>
                <span className="font-bold">
                  {gradePercentage !== undefined ? `${gradePercentage}%` : "-"}
                </span>
              </div>
            </>
          )}
        </div>

        {!!role && role != RoleType.Learner && (
          <div className="ml-auto flex flex-row items-center gap-4">
            {activityType === ActivityType.Assessment && (
              <>
                <span className="text-gray-800">
                  Calculated Points:
                  <span className="pl-1 font-bold">{pointsCalculated}</span>
                </span>
                <NumberField
                  disabled={modifierNumberIsDisabled}
                  hidePlaceholderOnFocus
                  maxValue={pointsAvailable || undefined}
                  minValue={
                    pointsCalculated !== undefined
                      ? -pointsCalculated
                      : undefined
                  }
                  numberSpinner
                  numberStep={modifierNumberStep}
                  onValueChange={handleNumberChange}
                  placeholder="0"
                  slotProps={{ input: { className: "w-8" } }}
                  value={pointsModifier?.toString() || ""}
                />
              </>
            )}

            {!activityIsCompleted && (
              <Button
                className="max-sm:px-4"
                disabled={
                  (activityType === ActivityType.Assessment &&
                    (pointsEarned === undefined || pointsEarned === null)) ||
                  activityStatus !== ActivityStatus.PendingGrade
                }
                loading={pendingSubmit}
                onClick={onSubmitActivityGrade}
                variant="contained"
              >
                Submit
              </Button>
            )}

            {!courseIsCompleted && activityIsCompleted && (
              <Button
                className="max-sm:px-4"
                loading={pendingEdit}
                onClick={onEditActivity}
                variant="contained"
              >
                Edit
              </Button>
            )}
            {dropDownItems && dropDownItems.length > 0 && (
              <DropDownMenu items={dropDownItems}>
                <button
                  aria-label="Open menu"
                  className="flex aspect-square w-10 cursor-pointer flex-col items-center justify-center space-y-0.5 rounded-3xl p-3 hover:bg-neutral-100"
                  type="button"
                >
                  <span className="h-1 w-1 rounded-full bg-gray-950" />
                  <span className="h-1 w-1 rounded-full bg-gray-950" />
                  <span className="h-1 w-1 rounded-full bg-gray-950" />
                </button>
              </DropDownMenu>
            )}
          </div>
        )}
      </div>

      {role && role != RoleType.Learner && resetConfirmModal && (
        <ConfirmDialog
          confirmBtnText="Reset"
          loading={pendingReset}
          onCancel={() => setResetConfirmModal(false)}
          onConfirm={() => onResetActivity?.()}
          show={resetConfirmModal}
          title="Reset"
        >
          <p>
            Are you sure you want to reset all answers in this{" "}
            {activityType?.toLowerCase()} {activityName}?
          </p>
        </ConfirmDialog>
      )}
    </>
  );
};
export default GradePanel;
